import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import type { ExportFormat } from '@/components/atoms/ExportButton/ExportButton';
import { Card } from '@/components/ui/card';
import { useModalTryIt } from '@/hooks/useModalTryIt';
import { AccountAnalyticsFilters } from '@/modules/analytics/components/filters/AccountAnalyticsFilters/AccountAnalyticsFilters';
import { getPermissions } from '@/redux/permissions/selectors';
import useSegments from '@/segment/useSegments';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { downloadCsv, downloadJson } from '@/util/fileManager';

import { FeedbackOverviewCard } from '../components/FeedbackOverview/FeedbackOverviewCard';
import { ChartError } from '../components/TimeseriesChart/TimeseriesChart';
import {
  UserCommentsTable,
  type UserCommentsSortKey,
} from '../components/UserComments/UserCommentsTable';
import { useUserFeedback } from '../hooks/useUserFeedback';
import { selectAnalyticsDates } from '../redux/selectors';
import { trackAnalyticsExport } from '../utils/analyticsTracking';
import { buildExportFilename } from '../utils/exportFilename';

const PAGE_SIZE = 10;
const EXPORT_ID = 'user_feedback_overview';

export const UserFeedback = () => {
  const { t } = useTranslation();
  useSegments(PageName.ANALYTICS_USER_FEEDBACK);
  const { startDate, endDate } = useSelector(selectAnalyticsDates);
  const { handleAccountReplayClick } = useModalTryIt();
  // Comments show customer names and text, so they need logs.read like the Logs page.
  const canReadLogs = useSelector((state: RootState) =>
    getPermissions(state, 'logs', actions.READ)
  );
  const [sort, setSort] = useState<{ key: UserCommentsSortKey; direction: 'asc' | 'desc' }>({
    key: 'date',
    direction: 'desc',
  });
  // The page belongs to one date range, so a new range starts back at page 0.
  const range = `${startDate}/${endDate}`;
  const [page, setPage] = useState({ range, index: 0 });
  const pageIndex = page.range === range ? page.index : 0;
  const setPageIndex = (index: number) => setPage({ range, index });
  const { totals, series, rows, total, isLoading, isLoadingRows, error, refetch } = useUserFeedback(
    { sort, pageIndex, pageSize: PAGE_SIZE, withComments: canReadLogs }
  );

  const handleSortChange = (key: UserCommentsSortKey) => {
    const flip = sort.key === key && sort.direction === 'desc';
    setSort({ key, direction: flip ? 'asc' : 'desc' });
    setPageIndex(0);
  };

  const handleExport = (format: ExportFormat) => {
    try {
      trackAnalyticsExport(PageName.ANALYTICS_USER_FEEDBACK, EXPORT_ID, format);
      const filename = buildExportFilename(EXPORT_ID);
      const kpi = {
        avg_csat: totals.avgCsat,
        rated_conversations: totals.ratedConversations,
        comments_sent: totals.commentsSent,
      };
      const timeseries = series.avgCsat.map((point, i) => ({
        date: point.date,
        avg_csat: point.value,
        csat_reviews: point.reviewCount,
        rated_conversations: series.ratedConversations[i].value,
      }));
      if (format === 'json') {
        downloadJson({ kpi, timeseries }, filename);
        return;
      }
      downloadCsv(
        [
          { row_type: 'kpi', ...kpi },
          ...timeseries.map((point) => ({ row_type: 'timeseries', ...point })),
        ],
        filename
      );
    } catch {
      toast.error(t('analytics.export_error'));
    }
  };

  return (
    <div className="mx-auto max-w-6xl">
      <AccountAnalyticsFilters page={PageName.ANALYTICS_USER_FEEDBACK} hideFilters />
      {error ? (
        <Card className="rounded-lg bg-background text-foreground shadow-none">
          <ChartError onRetry={refetch} t={t} />
        </Card>
      ) : (
        <div className="space-y-6">
          <FeedbackOverviewCard
            startDate={startDate}
            endDate={endDate}
            totals={totals}
            series={series}
            onExport={handleExport}
            isLoading={isLoading}
          />
          {canReadLogs && (
            <UserCommentsTable
              rows={rows}
              total={total}
              startDate={startDate}
              endDate={endDate}
              sort={sort}
              onSortChange={handleSortChange}
              pageIndex={pageIndex}
              pageSize={PAGE_SIZE}
              onPageChange={setPageIndex}
              onOpenConversation={(row) =>
                handleAccountReplayClick(row.sessionId, row.brainParentId, row.sessionStart)
              }
              isLoading={isLoadingRows}
            />
          )}
        </div>
      )}
    </div>
  );
};
